import {
  Card,
  Row,
  Table,
  Col,
  Input,
  message,
  Spin,
  Modal,
  Image,
  Select,
  Avatar,
  DatePicker,
  Tooltip,
  Popover,
  Dropdown,
  Pagination,
  Button,
  Menu,
  Form,
} from "antd";
import { RiAddBoxFill } from "react-icons/ri";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  ClockCircleOutlined,
  MessageOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useContext, useEffect, useMemo, useState } from "react";
import React from "react";
import {
  addBoard,
  deleteTask,
  fetchTasklog,
  getallBoard,
  getallDeveloperForTask,
  getAllTasksForCardView,
  updateBoardOrder,
  updateTask,
} from "../../utils/API";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  ChevronDown,
  ListTodo,
  LayoutDashboard,
  PencilLine,
  Trash2,
  Layout,
} from "lucide-react";
import TaskFormModal from "./Add"; // Ensure the path matches the actual file location
import EditTaskModal from "./Edit"; // Adjust path as needed
import { FaPen, FaTrashAlt } from "react-icons/fa";
import { AuthContext } from "../../Auth/AuthContext";

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

// Define the Developer interface based on API response
interface Task {
  details: any;
  _id: string;
  userId: string;
  boardId: string;
  taskName: any;
  taskDesc: string;
  startDate: string;
  endDate: string;
  deadlineDate: string;
  status: string;
  photo: string;
  priority: string;
  assignees: any[];
  reporter: string;
  username: string;
  createdAt: string;
  orderIndex: number;
}

interface Board {
  _id: any;
  name: string;
  slug: string;
}
interface EditTaskModal {
  visible: boolean;
  taskId: string;
  onSuccess: () => void;
  onCancel: () => void; // Make sure this is included
}

export const TasksTable = () => {
  const { authData } = useContext(AuthContext);
  const isAdmin = authData?.roleId?.roleId === 1;
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const taskIdFromUrl = searchParams.get('id'); // Get ?id= parameter
  
  const [dataSource, setDataSource] = useState<Task[]>([]);
  const [filteredData, setFilteredData] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [logSearchText, setLogSearchText] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const IMAGEURL = process.env.REACT_APP_IMAGE_URL + "/developers/";
  const [developerOptions, setDeveloperOptions] = useState<
    {
      photo: string;
      _id: string;
      username: string;
    }[]
  >([]);
  const [selectedTaskDesc, setSelectedTaskDesc] = useState<string | null>(null);
  const [tasklog, setTasklog] = useState<Task[]>([]);
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [selectedTaskLogs, setSelectedTaskLogs] = useState<any[]>([]);
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [currentTask, setCurrentTask] = useState<Task | null>(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [boardTitle, setBoardTitle] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false); // Add this line
  const [boards, setBoards] = useState<Board[]>([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const [loadingBoards, setLoadingBoards] = useState(false);
  const [selectedDeveloper, setSelectedDeveloper] = useState<string | null>(
    null,
  );
  const showTaskModal = () => setIsTaskModalOpen(true);
  const handleCancel = () => {
    setIsTaskModalOpen(false);
    setBoardTitle("");
  };

  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    setIsModalVisible(true);
  };

  const data = localStorage.getItem("authData");
  const userId = JSON.parse(data || "").user._id || null;
  const userRole = JSON.parse(data || "").roleId || null;

  const currentUser = () => {
    const data = localStorage.getItem("authData");
    if (!data) return null;

    const parsed = JSON.parse(data);
    return {
      ...parsed.user,
      role: parsed.roleId?.name?.toLowerCase() || "", // injects "admin", "developer", etc.
    };
  };

  const handleChange = (field: string, value: any) => {
    handleUpdate(
      selectedTask!._id,
      field,
      value,
      field.charAt(0).toUpperCase() + field.slice(1),
    );
    // Update local selectedTask state to reflect changes immediately
    setSelectedTask((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUpdate = async (
    taskId: string,
    field: string,
    updatedValue: any,
    label?: string,
  ) => {
    try {
      // Optimistically update the local state first
      const updatedDataSource = dataSource.map((task) => {
        if (task._id === taskId) {
          if (field === "status") {
            const selectedBoard = boards.find(
              (b: any) => b.slug === updatedValue,
            );
            if (selectedBoard) {
              const update = {
                status: updatedValue,
                boardId: selectedBoard._id,
              };

              const updatedTask = updatedDataSource.find(
                (task) => task._id === taskId,
              );
              if (updatedValue === "in-progress" && !updatedTask?.startDate) {
                updatePayload.startDate = new Date().toISOString();
              }

              return { ...task, ...update };
            }
          } else if (field === "boardId") {
            const selectedBoard = boards.find(
              (b: any) => b._id === updatedValue,
            );
            if (selectedBoard) {
              return {
                ...task,
                boardId: updatedValue,
                status: selectedBoard.slug,
              };
            }
          }
          return { ...task, [field]: updatedValue };
        }
        return task;
      });

      setDataSource(updatedDataSource);

      // Then make the API call
      let updatePayload: any = { [field]: updatedValue };

      if (field === "status") {
        const selectedBoard = boards.find((b: any) => b.slug === updatedValue);
        if (selectedBoard) {
          updatePayload = {
            status: updatedValue,
            boardId: selectedBoard._id,
          };

          const task = dataSource.find((task) => task._id === taskId);
          if (updatedValue === "in-progress" && !task?.startDate) {
            updatePayload.startDate = new Date().toISOString();
          }
        }
      } else if (field === "boardId") {
        const selectedBoard = boards.find((b: any) => b._id === updatedValue);
        if (selectedBoard) {
          updatePayload = {
            boardId: updatedValue,
            status: selectedBoard.slug,
          };
        }
      }

      await updateTask(taskId, updatePayload);

      // If needed, you can refresh the state from server here
      // But with optimistic updates, this might not be necessary
      message.success(`${label || field} updated successfully!`);
    } catch (error) {
      console.error(`Error updating ${field}`, error);
      message.error(`Failed to update ${label || field}`);

      // Revert the state if the API call fails
      setDataSource(dataSource);
    }
  };

  const getFilterData = (dataSource: any, developerId: any = userId) => {
    let filtered = dataSource;
    if (searchText.trim() !== "") {
      const lowercasedSearchText = searchText.toLowerCase();
      filtered = dataSource.filter((task: any) => {
        return (
          (typeof task.developerId === "string" &&
            task.developerId.toLowerCase().includes(lowercasedSearchText)) ||
          (typeof task.taskName === "string" &&
            task.taskName.toLowerCase().includes(lowercasedSearchText)) ||
          (typeof task.taskDesc === "string" &&
            task.taskDesc.toLowerCase().includes(lowercasedSearchText)) ||
          (typeof task.startDate === "string" &&
            task.startDate.toLowerCase().includes(lowercasedSearchText)) ||
          (typeof task.deadlineDate === "string" &&
            task.deadlineDate.toLowerCase().includes(lowercasedSearchText)) ||
          (typeof task.status === "string" &&
            task.status.toLowerCase().includes(lowercasedSearchText)) ||
          (typeof task.priority === "string" &&
            task.priority.toLowerCase().includes(lowercasedSearchText)) ||
          (Array.isArray(task.assignees) &&
            task.assignees.some((a: { username?: string }) =>
              a?.username?.toLowerCase().includes(lowercasedSearchText),
            )) || // ✅ Explicitly typed `a`
          (typeof task.reporter === "object" &&
            task.reporter?.username
              ?.toLowerCase()
              .includes(lowercasedSearchText)) // Ensure reporter is an object before accessing `firstName`
        );
      });
    }
    if (userRole.slug !== "admin") {
      const data = filtered.filter((task: any) =>
        task.assignees?.some((assignee: any) => assignee._id === developerId),
      );
      // setFilteredData(data);
      return data;
    } else {
      // setFilteredData(filtered);
      return filtered;
    }
  };
  const fetchBoards = async () => {
    try {
      const response = await getallBoard("", page); // Your API call to get boards
      const sortedBoards = response.data.sort(
        (a: any, b: any) => a.orderIndex - b.orderIndex,
      );
      setBoards(sortedBoards);
    } catch (error) {
      console.error("Error fetching boards:", error);
    }
  };
  useEffect(() => {
    fetchBoards();
  }, [page]);
  const handleAddBoard = async () => {
    if (boardTitle.trim()) {
      try {
        const payload = { name: boardTitle.trim() };
        await addBoard(payload);
        fetchBoards(); // Refresh board list from backend
        setBoardTitle("");
        setIsTaskModalOpen(false);
      } catch (error) {
        console.error("Error adding board", error);
      }
    }
  };
  const fetchTasks = async (developerId: string | null = null) => {
    setLoading(true);
    try {
      const { role } = currentUser();
      const developerIdToUse =
        developerId ||
        (currentUser().role === "admin" ? null : currentUser()._id);
      // Pass developerId to the API call
      console.log("Search Text", searchText);
      const response: any = await getAllTasksForCardView(
        searchText,
        developerIdToUse,
        role,
      );

      if (response && response.success) {
        const sortedTasks = response.data
          .sort(
            (a: any, b: any) =>
              new Date(b.createdAt || b.startDate).getTime() -
              new Date(a.createdAt || a.startDate).getTime(),
          )
          .map((item: any, index: number) => ({
            ...item,
            serialNumber: index + 1,
          }));

        setDataSource(sortedTasks);
        
        // ✅ Filter by task ID if present in URL
        if (taskIdFromUrl) {
          const specificTask = sortedTasks.filter((task: any) => task._id === taskIdFromUrl);
          setFilteredData(specificTask);
        } else {
          setFilteredData(sortedTasks); // No client-side filtering needed now
        }
      } else {
        if (
          response?.status === 500 ||
          response?.message === "Internal Server Error"
        ) {
          message.error("Failed to fetch tasks due to server error.");
        }
        setDataSource([]);
        setFilteredData([]);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
      message.error("Network error. Please try again.");
      setDataSource([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeveloperSelect = (developerId: string | null) => {
    setSelectedDeveloper(developerId);
    fetchTasks(developerId); // Trigger API call with new filter
  };

  const groupedTasks = useMemo(() => {
    return filteredData.reduce((acc: { [key: string]: Task[] }, task: Task) => {
      const key = task.status;
      if (!acc[key]) acc[key] = [];
      acc[key].push(task);
      return acc;
    }, {});
  }, [filteredData]);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const response: any = await fetchTasklog("", page, "", 5); // Fetch task logs
        setTasklog(response); // Force update before filtering
        // console.log("Logs API response", response);
        if (response && Array.isArray(response)) {
          setTasklog(response); // ✅ Store raw logs
        } else {
          setTasklog([]); // Handle empty or incorrect data
          console.error("Unexpected task log format:", response);
        }
      } catch (error) {
        console.error("Error fetching task logs:", error);
      }
    };
    fetchLogs();
  }, []);
  // Fetch developers for dropdown options
  useEffect(() => {
    const fetchDevelopers = async () => {
      try {
        const response: any = await getallDeveloperForTask("", ""); // Fetch developers
        if (response?.data) {
          setDeveloperOptions(response.data); // Store in state
        } else {
          setDeveloperOptions([]);
        }
      } catch (error) {
        // console.error("Error fetching developers:", error);
      }
    };
    fetchDevelopers();
  }, []);
  useEffect(() => {
    fetchTasks();
  }, [searchText, selectedDeveloper, userId]);
  // Handle Delete Action
  const handleDelete = async () => {
    if (deleteId) {
      try {
        const response = await deleteTask(deleteId, true);
        if (response) {
          message.success("Task deleted successfully");
          fetchTasks(); // Refresh list
        } else {
          message.error("Failed to delete task");
        }
      } catch (error) {
        message.error("Error deleting task  . Please try again.");
      } finally {
        setDeleteId(null); // Close the confirmation modal
      }
    }
  };
  const handleEditTask = (task: Task, e?: React.MouseEvent) => {
    e?.stopPropagation(); // Stop event propagation
    setCurrentTaskId(task._id);
    setEditModalVisible(true);
    // Don't open the details modal
    setIsModalVisible(false); // Ensure details modal is closed
  };
  const handleEditSuccess = () => {
    setEditModalVisible(false);
    fetchTasks(); // Refresh the task list
  };

  const onDragEnd = async (result: any) => {
    const { source, destination, draggableId, type } = result;
    if (!destination) return;

    // Handle board reordering
    if (type === "BOARD") {
      const reorderedBoards = Array.from(boards);
      const [movedBoard] = reorderedBoards.splice(source.index, 1);
      reorderedBoards.splice(destination.index, 0, movedBoard);
      const updatedBoards = reorderedBoards.map((board, index) => ({
        ...board,
        orderIndex: index,
      }));
      setBoards(reorderedBoards);
      try {
        await updateBoardOrder(updatedBoards); // Call your new API endpoint
      } catch (error) {
        message.error("Failed to save board order");
      }
      return;
    }

    // Ignore if position hasn’t changed
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    )
      return;

    const updatedDataSource = [...dataSource];
    const movedTask = updatedDataSource.find(
      (task) => task._id === draggableId,
    );
    if (!movedTask) return;

    const sourceBoardId = source.droppableId;
    const destBoardId = destination.droppableId;

    // Collect affected tasks from both source and destination boards
    const affectedTasks = updatedDataSource.filter(
      (task) => task.boardId === sourceBoardId || task.boardId === destBoardId,
    );

    // Remove dragged task
    const remainingTasks = affectedTasks.filter(
      (task) => task._id !== draggableId,
    );

    // Update boardId of moved task if it changed
    if (sourceBoardId !== destBoardId) {
      movedTask.boardId = destBoardId;
    }

    // Insert moved task at the destination index
    remainingTasks.splice(destination.index, 0, movedTask);

    // Reassign orderIndex to all affected tasks
    const reorderedTasks = remainingTasks.map((task, index) => ({
      ...task,
      orderIndex: index,
    }));

    // Replace affected tasks in the dataSource
    const finalDataSource = updatedDataSource.map((task) => {
      const updated = reorderedTasks.find((t) => t._id === task._id);
      return updated || task;
    });

    setDataSource(finalDataSource);
    setFilteredData(getFilterData(finalDataSource));

    try {
      const inProgressBoard = boards.find((b) => b.name === "In Progress");
      const completedBoard = boards.find((b) => b.name === "Completed");

      await Promise.all(
        reorderedTasks.map((task) => {
          const updatePayload: any = {
            boardId: task.boardId,
            orderIndex: task.orderIndex,
          };

          // Set startDate if dragged to "In Progress"
          if (
            task._id === draggableId &&
            inProgressBoard &&
            task.boardId === inProgressBoard._id &&
            !task.startDate
          ) {
            updatePayload.startDate = new Date().toISOString();
          }

          // Set endDate if dragged to "Completed"
          if (
            task._id === draggableId &&
            completedBoard &&
            task.boardId === completedBoard._id
          ) {
            const now = new Date().toISOString();
            if (!task.startDate) {
              updatePayload.startDate = now;
            }
            if (!task.endDate) {
              updatePayload.endDate = now;
            }
          }
          return updateTask(task._id, updatePayload);
        }),
      );
    } catch (error) {
      message.error("Failed to move task. Please try again.");
    }
  };

  const menuItems = [
    {
      key: "table",
      label: (
        <div className="flex items-center gap-2 px-2 py-1 rounded-md group transition-all">
          <div className="w-7 h-7 flex items-center justify-center rounded-md bg-[#001529] text-white group-hover:bg-[#112240] shadow-sm transition-all">
            <ListTodo size={16} />
          </div>
          <span className="text-sm text-gray-800 font-medium">Table View</span>
        </div>
      ),
      onClick: () => navigate("/tasks"),
    },
    {
      key: "card",
      label: (
        <div className="flex items-center gap-2 px-2 py-1 rounded-md group transition-all">
          <div className="w-7 h-7 flex items-center justify-center rounded-md bg-[#001529] text-white group-hover:bg-[#112240] shadow-sm transition-all">
            <LayoutDashboard size={16} />
          </div>
          <span className="text-sm text-gray-800 font-medium">Card View</span>
        </div>
      ),
      onClick: () => navigate("/task"),
    },
  ];

  return (
    <div>
      {/* Header & Search */}
      <div className="overflow-x-auto w-full">
        <Row
          gutter={[16, 16]}
          align="middle"
          justify="space-between"
          wrap={false}
          className="min-w-[1000px] m-1"
        >
          {/* Left Section */}
          <Col xs={24} sm={24} md={6} lg={6} xl={6}>
            <h2 className="headername">Tasks</h2>
          </Col>

          {/* Right Section */}
          <Col>
            <Row className="flex items-center gap-2" align="middle">
              <Col>
                {isAdmin && (
                  <div
                    className="flex items-center gap-2"
                    style={{ height: "33px" }}
                  >
                    {/* Developer Avatars */}
                    <div className="flex items-center space-x-[-10px]">
                      {developerOptions.slice(0, 6).map((developer) => (
                        <Tooltip title={developer.username} key={developer._id}>
                          <div
                            className={`w-8 h-8 rounded-full border-2 ${
                              selectedDeveloper === developer._id
                                ? "border-blue-500 ring-2 ring-blue-300"
                                : "border-white"
                            } overflow-hidden transition-transform hover:z-10 hover:scale-110 cursor-pointer`}
                            onClick={() =>
                              handleDeveloperSelect(
                                selectedDeveloper === developer._id
                                  ? null
                                  : developer._id,
                              )
                            }
                          >
                            {developer.photo ? (
                              <img
                                src={`${IMAGEURL}/${developer.photo}`}
                                alt={developer.username}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="bg-gray-300 w-full h-full flex items-center justify-center text-xs font-bold text-white">
                                {developer.username
                                  .trim()
                                  .charAt(0)
                                  .toUpperCase()}
                              </div>
                            )}
                          </div>
                        </Tooltip>
                      ))}

                      {/* Additional Developers Dropdown */}
                      {developerOptions.length > 6 && (
                        <Dropdown
                          overlay={
                            <div className="bg-white border border-gray-200 rounded-md shadow-lg p-2 w-64 max-h-80 overflow-y-auto">
                              {developerOptions.slice(6).map((developer) => (
                                <div
                                  key={developer._id}
                                  className={`flex items-center space-x-2 py-2 px-3 rounded cursor-pointer hover:bg-gray-100 ${
                                    selectedDeveloper === developer._id
                                      ? "bg-blue-50"
                                      : ""
                                  }`}
                                  onClick={() =>
                                    handleDeveloperSelect(
                                      selectedDeveloper === developer._id
                                        ? null
                                        : developer._id,
                                    )
                                  }
                                >
                                  {developer.photo ? (
                                    <img
                                      src={`${IMAGEURL}/${developer.photo}`}
                                      alt={developer.username}
                                      className="w-6 h-6 rounded-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-xs font-bold text-white">
                                      {developer.username
                                        .trim()
                                        .charAt(0)
                                        .toUpperCase()}
                                    </div>
                                  )}
                                  <span className="text-sm text-gray-700 flex-grow">
                                    {developer.username}
                                  </span>
                                  {selectedDeveloper === developer._id && (
                                    <span className="text-blue-500">
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          }
                          trigger={["click"]}
                          placement="bottomRight"
                        >
                          <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-600 text-xs font-semibold flex items-center justify-center border-2 border-white cursor-pointer hover:bg-gray-300">
                            +{developerOptions.length - 6}
                          </div>
                        </Dropdown>
                      )}
                    </div>

                    {/* Selected Developer Avatar with Deselect Button */}
                    {selectedDeveloper && (
                      <div className="flex items-center space-x-1 ml-2">
                        <div className="relative w-8 h-8 rounded-full border-2 border-blue-500 ring-2 ring-blue-300 overflow-hidden">
                          {(() => {
                            const dev = developerOptions.find(
                              (d) => d._id === selectedDeveloper,
                            );
                            if (!dev) return null;
                            return dev.photo ? (
                              <img
                                src={`${IMAGEURL}/${dev.photo}`}
                                alt={dev.username}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="bg-gray-300 w-full h-full flex items-center justify-center text-xs font-bold text-white">
                                {dev.username.trim().charAt(0).toUpperCase()}
                              </div>
                            );
                          })()}
                          <button
                            onClick={() => handleDeveloperSelect(null)}
                            className="absolute -top-1 -right-1 bg-white text-gray-500 hover:text-red-500 rounded-full w-4 h-4 flex items-center justify-center text-[10px] shadow"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Col>
              {/* Search Input */}
              <Dropdown
                overlay={<Menu items={menuItems} />}
                trigger={["click"]}
              >
                <Tooltip title="Views">
                  <button className="w-9 h-9 rounded-full bg-[#001529] hover:bg-[#112240] transition duration-200 shadow-md flex items-center justify-center group">
                    <Layout
                      className="text-white group-hover:text-blue-300 transition-transform duration-200 group-hover:scale-110"
                      size={18}
                    />
                  </button>
                </Tooltip>
              </Dropdown>

              <Col>
                <Input
                  prefix={<SearchOutlined />}
                  size="large"
                  placeholder="Search..."
                  allowClear
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="min-w-[150px]"
                  style={{ height: "33px" }}
                />
              </Col>
              {/* New Board Button */}

              <Button
                type="primary"
                className="allbuttons"
                onClick={showTaskModal}
              >
                <RiAddBoxFill />
                <span className="ms-2">New Board</span>
              </Button>

              <Col>
                {/* Add Task Button */}
                <Button
                  type="primary"
                  onClick={() => setIsAddTaskModalOpen(true)}
                  className="allbuttons"
                >
                  <RiAddBoxFill />
                  <div className="ms-2">Add</div>
                </Button>

                {/* Add Task Modal */}
                {isAddTaskModalOpen && (
                  <TaskFormModal
                    visible={isAddTaskModalOpen}
                    onClose={() => setIsAddTaskModalOpen(false)}
                    onTaskCreated={() => {
                      setIsAddTaskModalOpen(false);
                      fetchTasks(); // Refresh the task list
                    }}
                  />
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
      {loadingBoards ? (
        <div className="text-center text-gray-500 mt-10">Loading boards...</div>
      ) : (
        <Spin spinning={loading}>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="boards" direction="horizontal" type="BOARD">
              {(provided) => (
                <div
                  className="flex gap-4 overflow-x-auto mt-6 pb-2"
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {boards.map((board: any, boardIndex: number) => {
                    const boardTasks = filteredData
                      .filter((task) => task.boardId === board._id)
                      .sort((a, b) => a.orderIndex - b.orderIndex);

                    return (
                      <Draggable
                        draggableId={`board-${board._id}`}
                        index={boardIndex}
                        key={board._id}
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <Droppable droppableId={board._id} type="TASK">
                              {(provided) => (
                                <div
                                  className="bg-white border border-gray-200 rounded-2xl p-3 shadow-sm flex flex-col h-[85vh] w-80 flex-shrink-0 transition-all duration-200 hover:shadow-md"
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                >
                                  <h3 className="text-lg font-bold text-[#001529] mb-4 border-b pb-2">
                                    {board.name}
                                  </h3>

                                  <div
                                    className="space-y-3 pr-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300"
                                    style={{
                                      maxHeight: "calc(85vh - 90px)",
                                      minHeight: "40px",
                                    }}
                                  >
                                    {boardTasks.length > 0 ? (
                                      boardTasks.map(
                                        (task: any, taskIndex: number) => (
                                          <Draggable
                                            draggableId={String(task._id)}
                                            index={taskIndex}
                                            key={task._id}
                                          >
                                            {(provided) => (
                                              <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                onClick={() =>
                                                  handleTaskClick(task)
                                                }
                                                className={`relative rounded-md p-4 shadow-md transition-all duration-200 min-h-[90px] cursor-pointer border-l-4
                                      ${
                                        task.priority === "High"
                                          ? "border-red-500 bg-red-50 hover:border-red-500"
                                          : task.priority === "Medium"
                                            ? "border-yellow-400 bg-yellow-50 hover:border-yellow-500"
                                            : task.priority === "Low"
                                              ? "border-green-500 bg-green-50 hover:border-green-500"
                                              : "border-gray-300 bg-gray-50 hover:border-gray-300"
                                      }
                                      hover:border hover:border-solid hover:border-opacity-100`}
                                              >
                                                {/* Top Section: Created & Deadline */}
                                                <div className="flex justify-between text-xs text-gray-400 mb-1">
                                                  <span>
                                                    <ClockCircleOutlined />{" "}
                                                    {task.createdAt
                                                      ? dayjs(
                                                          task.createdAt,
                                                        ).format("DD MMM")
                                                      : ""}
                                                  </span>
                                                  <span
                                                    className={`font-semibold ${
                                                      task.status ===
                                                      "completed"
                                                        ? task.deadlineDate &&
                                                          dayjs(
                                                            task.deadlineDate,
                                                          ).isBefore(
                                                            dayjs(
                                                              task.endDate ||
                                                                dayjs(),
                                                            ),
                                                          )
                                                          ? "text-red-500" // Completed, but deadline was missed
                                                          : "text-green-700" // Completed on time
                                                        : task.deadlineDate &&
                                                            dayjs(
                                                              task.deadlineDate,
                                                            ).isBefore(
                                                              dayjs(),
                                                            ) &&
                                                            !task.endDate
                                                          ? "text-red-500" // Not completed and overdue
                                                          : "text-green-700" // Ongoing and still within deadline
                                                    }`}
                                                  >
                                                    {task.status === "completed"
                                                      ? "Completed"
                                                      : task.deadlineDate
                                                        ? (() => {
                                                            const now = dayjs();
                                                            const deadline =
                                                              dayjs(
                                                                task.deadlineDate,
                                                              );
                                                            if (
                                                              !deadline.isValid()
                                                            )
                                                              return "Invalid date";

                                                            const diffMinutes =
                                                              deadline.diff(
                                                                now,
                                                                "minute",
                                                              );
                                                            const overdueMinutes =
                                                              Math.abs(
                                                                diffMinutes,
                                                              );

                                                            if (
                                                              diffMinutes >=
                                                              1440
                                                            )
                                                              return `${Math.floor(diffMinutes / 1440)}d left`;
                                                            if (
                                                              diffMinutes >= 60
                                                            )
                                                              return `${Math.floor(diffMinutes / 60)}h left`;
                                                            if (diffMinutes > 0)
                                                              return `${diffMinutes}m left`;

                                                            if (
                                                              overdueMinutes <
                                                              60
                                                            )
                                                              return `${overdueMinutes}m overdue`;
                                                            if (
                                                              overdueMinutes <
                                                              1440
                                                            )
                                                              return `${Math.floor(overdueMinutes / 60)}h overdue`;
                                                            return `${Math.floor(overdueMinutes / 1440)}d overdue`;
                                                          })()
                                                        : "No deadline"}
                                                  </span>
                                                </div>

                                                {/* Title and Priority */}
                                                <div className="flex items-center justify-between mb-1">
                                                  <h3 className="font-semibold text-gray-800 text-sm line-clamp-1">
                                                    {task.taskName}
                                                  </h3>
                                                  <span
                                                    className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide
                                          ${
                                            task.priority === "High"
                                              ? "bg-red-600 text-white"
                                              : task.priority === "Medium"
                                                ? "bg-yellow-400 text-white"
                                                : task.priority === "Low"
                                                  ? "bg-green-500 text-white"
                                                  : "bg-gray-400 text-white"
                                          }`}
                                                  >
                                                    {task.priority || "-"}
                                                  </span>
                                                </div>

                                                {/* Description */}
                                                <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                                                  {task.taskDesc}
                                                </p>

                                                {/* Assignees + Actions */}

                                                <div className="flex justify-between items-center border-t pt-2">
                                                  <div className="flex -space-x-2">
                                                    {task.assignees
                                                      ?.slice(0, 2)
                                                      .map((user: any) => (
                                                        <Tooltip
                                                          title={user.username.trim()}
                                                          placement="top"
                                                          color="#CCCCCC"
                                                          overlayInnerStyle={{
                                                            color: "#000", // ✅ font color
                                                            padding: "12px",
                                                          }}
                                                        >
                                                          <div
                                                            key={user._id}
                                                            className="w-6 h-6 rounded-full border-2 border-white overflow-hidden"
                                                          >
                                                            {user.photo ? (
                                                              <img
                                                                src={`${IMAGEURL}/${user.photo}`}
                                                                alt={
                                                                  user.username
                                                                }
                                                                className="w-full h-full object-cover"
                                                              />
                                                            ) : (
                                                              <div className="bg-gray-300 w-full h-full flex items-center justify-center text-[10px] font-bold text-white">
                                                                {user.username
                                                                  .trim()
                                                                  .charAt(0)
                                                                  .toUpperCase()}
                                                              </div>
                                                            )}
                                                          </div>
                                                        </Tooltip>
                                                      ))}
                                                    {task.assignees.length >
                                                      2 && (
                                                      <div className="relative group">
                                                        <div className="w-6 h-6 rounded-full bg-gray-500 text-white text-[10px] font-semibold flex items-center justify-center border-2 border-white cursor-pointer">
                                                          +
                                                          {task.assignees
                                                            .length - 2}
                                                        </div>
                                                        <div className="absolute bottom-8 right-0 bg-white border border-gray-200 rounded-md shadow-lg p-2 z-10 min-w-[120px] hidden group-hover:flex flex-col transition-opacity duration-200 opacity-0 group-hover:opacity-100">
                                                          {task.assignees
                                                            .slice(2)
                                                            .map(
                                                              (user: any) => (
                                                                <div
                                                                  key={user._id}
                                                                  className="flex items-center space-x-2 py-1 px-2 hover:bg-gray-50 rounded"
                                                                >
                                                                  {user.photo ? (
                                                                    <img
                                                                      src={`${IMAGEURL}/${user.photo}`}
                                                                      alt={
                                                                        user.username
                                                                      }
                                                                      className="w-5 h-5 rounded-full object-cover"
                                                                    />
                                                                  ) : (
                                                                    <div className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center text-[10px] font-bold text-white">
                                                                      {user.username
                                                                        .trim()
                                                                        .charAt(
                                                                          0,
                                                                        )
                                                                        .toUpperCase()}
                                                                    </div>
                                                                  )}
                                                                  <span className="text-xs text-gray-700">
                                                                    {
                                                                      user.username
                                                                    }
                                                                  </span>
                                                                </div>
                                                              ),
                                                            )}
                                                        </div>
                                                      </div>
                                                    )}
                                                  </div>
                                                  <Dropdown
                                                    overlay={
                                                      <Menu className="rounded-lg overflow-hidden shadow-xl border border-gray-200 min-w-[120px]">
                                                        <Menu.Item
                                                          key="edit"
                                                          className="group !py-2 !px-3 hover:!bg-emerald-50/80 transition-colors duration-150"
                                                          icon={
                                                            <div
                                                              className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 
                                                          shadow-[0_4px_12px_rgba(139,92,246,0.3)] 
                                                          hover:shadow-[0_6px_16px_rgba(139,92,246,0.4)] 
                                                          active:translate-y-[1px] active:shadow-none 
                                                          text-white flex items-center justify-center 
                                                          transition-all duration-200 ease-in-out group"
                                                            >
                                                              <FaPen
                                                                className="group-hover:scale-110 transition-transform duration-200"
                                                                size={12}
                                                              />
                                                            </div>
                                                          }
                                                          onClick={(e) => {
                                                            e.domEvent.stopPropagation();
                                                            handleEditTask(
                                                              task,
                                                            );
                                                          }}
                                                        >
                                                          <span className="font-medium text-gray-800 text-sm">
                                                            Edit
                                                          </span>
                                                        </Menu.Item>
                                                        <Menu.Item
                                                          key="delete"
                                                          className="group !py-2 !px-3 hover:!bg-rose-50/80 transition-colors duration-150"
                                                          icon={
                                                            <div
                                                              className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500 to-rose-700 
                                                            shadow-[0_4px_12px_rgba(244,63,94,0.3)] 
                                                            hover:shadow-[0_6px_16px_rgba(244,63,94,0.4)] 
                                                            active:translate-y-1 active:shadow-none 
                                                          text-white flex items-center justify-center 
                                                            transition-all duration-200 group"
                                                            >
                                                              <FaTrashAlt
                                                                className="group-hover:scale-110 transition-transform"
                                                                size={12}
                                                              />
                                                            </div>
                                                          }
                                                          onClick={(e) => {
                                                            e.domEvent.stopPropagation();
                                                            setDeleteId(
                                                              task._id,
                                                            );
                                                          }}
                                                        >
                                                          <span className="font-medium text-gray-800 text-sm">
                                                            Delete
                                                          </span>
                                                        </Menu.Item>
                                                      </Menu>
                                                    }
                                                    trigger={["click"]}
                                                    placement="bottomRight"
                                                    overlayClassName="action-dropdown-overlay"
                                                  >
                                                    <Button
                                                      type="text"
                                                      icon={
                                                        <div className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center transition-all duration-200 group">
                                                          <svg
                                                            width="16"
                                                            height="16"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            className="text-gray-500 group-hover:text-gray-700 transition-colors"
                                                            strokeWidth="2"
                                                          >
                                                            <circle
                                                              cx="12"
                                                              cy="12"
                                                              r="1"
                                                            />
                                                            <circle
                                                              cx="12"
                                                              cy="5"
                                                              r="1"
                                                            />
                                                            <circle
                                                              cx="12"
                                                              cy="19"
                                                              r="1"
                                                            />
                                                          </svg>
                                                        </div>
                                                      }
                                                      className="!p-0 !h-auto !w-auto hover:!bg-transparent"
                                                      onClick={(e) =>
                                                        e.stopPropagation()
                                                      }
                                                    />
                                                  </Dropdown>
                                                </div>
                                              </div>
                                            )}
                                          </Draggable>
                                        ),
                                      )
                                    ) : (
                                      <p className="text-xs text-gray-400 text-center py-4">
                                        No tasks available
                                      </p>
                                    )}
                                    {provided.placeholder}
                                  </div>
                                </div>
                              )}
                            </Droppable>
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Spin>
      )}
      <Modal
        open={isTaskModalOpen}
        onCancel={handleCancel}
        footer={null}
        width={600}
        centered
        destroyOnClose
        className="custom-modal"
        style={{
          // top: 20,
          maxHeight: "100%",
          margin: "0 auto",
          overflow: "hidden",
          position: "relative",
        }}
        bodyStyle={{
          maxHeight: "calc(90vh - 55px)", // Adjusted for header
          overflowY: "auto",
          padding: "0.25px",
          fontFamily: "inter, sans-serif",
          // fontWeight: 500
        }}
        styles={{
          header: {
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "#f1f5ffff",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
        }}
        title={
          <div
            style={{
              color: "#4b5563",
              fontFamily: "inter, sans-serif",
              fontSize: "20px",
              fontWeight: 500,
            }}
          >
            Add New Task Board
          </div>
        }
        // onOk={handleSuccess}
      >
        <Form
          layout="horizontal"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 19 }}
          colon={false}
        >
          <div className="elements ">
            <Form.Item
              label={
                <span
                  style={{
                    color: "#4b5563",
                    fontFamily: "inter, sans-serif",
                    fontSize: "14px",
                    paddingLeft: "15px",
                    fontWeight: 500,
                  }}
                >
                  Task board Title<span className="text-red-500">*</span>
                </span>
              }
              required={false}
              name="boardTitle"
              rules={[{ required: true, message: "Please enter board title!" }]}
            >
              <Input
                placeholder="Enter board title"
                className="Allinput"
                value={boardTitle}
                onChange={(e) => setBoardTitle(e.target.value)}
              />
            </Form.Item>
          </div>
        </Form>

        <Col
          xs={24}
          sm={24}
          md={24}
          xl={24}
          xxl={20}
          className="d-flex justify-content-end me-3"
        >
          <div className="button-wrapper">
            <button
              className="allcanclebutton"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              className="allsubmitebutton"
              onClick={handleAddBoard}
              disabled={isLoading}
            >
              {isLoading ? "Add Board..." : "Add Board"}
            </button>
          </div>
        </Col>
      </Modal>
      <Modal
        key={selectedTask?._id || "no-task"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        afterClose={() => getAllTasksForCardView("", "")}
        footer={null}
        width={980}
      >
        {selectedTask ? (
          /* Trello-like board background */
          <div className="relative space-y-6 rounded-2xl bg-sky-50/70 p-6">
            {/* Board Header (like Trello board bar) */}
            <div className="flex items-center justify-between rounded-xl bg-sky-200/60 px-5 py-3 ring-1 ring-sky-300">
              <div className="min-w-0">
                <h2 className="truncate text-xl font-semibold tracking-tight text-sky-900">
                  {selectedTask.taskName || "Untitled Task"}
                </h2>
                <p className="mt-0.5 text-xs text-sky-900/70">
                  Created on{" "}
                  <span className="font-medium">
                    {selectedTask.createdAt
                      ? dayjs(selectedTask.createdAt).format("DD MMM YYYY")
                      : "Unknown"}
                  </span>
                </p>
              </div>

              {/* Trello-like solid label chip for priority */}
              <span
                className={[
                  "inline-flex items-center rounded-full px-3 py-1 text-xs font-medium shadow-sm",
                  "bg-gradient-to-r from-indigo-100 via-purple-100 to-pink-100 text-indigo-700 ring-1 ring-indigo-200",
                ].join(" ")}
              >
                {boards.find((b) => b._id === selectedTask.boardId)?.name ||
                  "No Board"}
              </span>
            </div>

            {/* Trello columns area */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {/* Column 1: Description (Trello card style) */}
              <div className="rounded-lg bg-white shadow-sm ring-1 ring-black/5">
                <div className="flex items-center gap-2 border-b border-neutral-200 px-4 py-3">
                  <span className="h-3 w-3 rounded-[3px] bg-indigo-500" />
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-700">
                    Description
                  </h3>
                </div>
                <div className="p-4">
                  <div className="min-h-[120px] whitespace-pre-line rounded-md bg-neutral-50 p-4 text-sm leading-relaxed text-neutral-800 ring-1 ring-neutral-200">
                    {selectedTask.taskDesc || "No description provided."}
                  </div>
                </div>
              </div>

              {/* Column 2: Timeline (Trello card style with badges) */}
              <div className="rounded-lg bg-white shadow-sm ring-1 ring-black/5">
                <div className="flex items-center gap-2 border-b border-neutral-200 px-4 py-3">
                  <span className="h-3 w-3 rounded-[3px] bg-fuchsia-500" />
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-700">
                    Timeline
                  </h3>
                </div>
                <div className="p-4 text-sm text-neutral-800">
                  <ul className="space-y-3">
                    {/* Start */}
                    <li className="flex items-start justify-between rounded-md bg-neutral-50 p-3 ring-1 ring-neutral-200">
                      <span className="font-medium text-neutral-700">
                        Start
                      </span>
                      <span className="text-right">
                        {selectedTask.startDate &&
                        dayjs(selectedTask.startDate).isValid() ? (
                          <>
                            <span className="block font-medium text-neutral-900">
                              {dayjs(selectedTask.startDate).format(
                                "DD-MM-YYYY",
                              )}
                            </span>
                            <span className="text-[11px] text-sky-600">
                              {dayjs(selectedTask.startDate).format("hh:mm A")}
                            </span>
                          </>
                        ) : (
                          <span className="text-neutral-400">Not set</span>
                        )}
                      </span>
                    </li>

                    {/* End */}
                    <li className="flex items-start justify-between rounded-md bg-neutral-50 p-3 ring-1 ring-neutral-200">
                      <span className="font-medium text-neutral-700">End</span>
                      <span className="text-right">
                        {selectedTask.endDate &&
                        dayjs(selectedTask.endDate).isValid() ? (
                          <>
                            <span className="block font-medium text-neutral-900">
                              {dayjs(selectedTask.endDate).format("DD-MM-YYYY")}
                            </span>
                            <span className="text-[11px] text-sky-600">
                              {dayjs(selectedTask.endDate).format("hh:mm A")}
                            </span>
                          </>
                        ) : (
                          <span className="text-neutral-400">Not set</span>
                        )}
                      </span>
                    </li>

                    {/* Deadline */}
                    <li className="space-y-2 rounded-md bg-neutral-50 p-3 ring-1 ring-neutral-200">
                      <div className="flex items-start justify-between">
                        <span className="font-medium text-neutral-700">
                          Deadline
                        </span>
                        <span className="text-right">
                          {selectedTask.deadlineDate &&
                          dayjs(selectedTask.deadlineDate).isValid() ? (
                            <>
                              <span className="block font-medium text-neutral-900">
                                {dayjs(selectedTask.deadlineDate).format(
                                  "DD-MM-YYYY",
                                )}
                              </span>
                              <span className="text-[11px] text-sky-600">
                                {dayjs(selectedTask.deadlineDate).format(
                                  "hh:mm A",
                                )}
                              </span>
                            </>
                          ) : (
                            <span className="text-neutral-400">Not set</span>
                          )}
                        </span>
                      </div>

                      {/* Trello-style status badge */}
                      {selectedTask.deadlineDate &&
                        dayjs(selectedTask.deadlineDate).isValid() && (
                          <>
                            {selectedTask.status === "completed"
                              ? (() => {
                                  const deadline = dayjs(
                                    selectedTask.deadlineDate,
                                  );
                                  const endDate = dayjs(selectedTask.endDate);
                                  const isLate = endDate.isAfter(deadline);
                                  return (
                                    <span
                                      className={[
                                        "inline-flex rounded-[6px] px-2 py-1 text-[11px] font-semibold uppercase tracking-wide",
                                        isLate
                                          ? "bg-rose-500 text-white"
                                          : "bg-emerald-500 text-white",
                                      ].join(" ")}
                                    >
                                      Completed
                                    </span>
                                  );
                                })()
                              : (() => {
                                  const deadline = dayjs(
                                    selectedTask.deadlineDate,
                                  );
                                  const now = dayjs();
                                  const isOverdue = now.isAfter(deadline);
                                  const totalMinutes = deadline.diff(
                                    now,
                                    "minute",
                                  );
                                  const absoluteMinutes =
                                    Math.abs(totalMinutes);

                                  let timeInfo = "";
                                  if (isOverdue) {
                                    if (absoluteMinutes >= 1440) {
                                      const days = Math.floor(
                                        absoluteMinutes / 1440,
                                      );
                                      const hours = Math.floor(
                                        (absoluteMinutes % 1440) / 60,
                                      );
                                      timeInfo = `${days}d${hours ? ` ${hours}h` : ""} overdue`;
                                    } else if (absoluteMinutes >= 60) {
                                      const hours = Math.floor(
                                        absoluteMinutes / 60,
                                      );
                                      const mins = absoluteMinutes % 60;
                                      timeInfo = `${hours}h${mins ? ` ${mins}m` : ""} overdue`;
                                    } else {
                                      timeInfo = `${absoluteMinutes}m overdue`;
                                    }
                                  } else {
                                    if (totalMinutes > 1440) {
                                      const days = Math.floor(
                                        totalMinutes / 1440,
                                      );
                                      const hours = Math.floor(
                                        (totalMinutes % 1440) / 60,
                                      );
                                      timeInfo = `${days}d${hours ? ` ${hours}h` : ""} left`;
                                    } else if (totalMinutes > 60) {
                                      const hours = Math.floor(
                                        totalMinutes / 60,
                                      );
                                      const mins = totalMinutes % 60;
                                      timeInfo = `${hours}h${mins ? ` ${mins}m` : ""} left`;
                                    } else {
                                      timeInfo = `${totalMinutes}m left`;
                                    }
                                  }

                                  return (
                                    <span
                                      className={[
                                        "inline-flex rounded-[6px] px-2 py-1 text-[11px] font-semibold uppercase tracking-wide",
                                        isOverdue
                                          ? "bg-rose-500 text-white"
                                          : "bg-amber-500 text-white",
                                      ].join(" ")}
                                    >
                                      {timeInfo}
                                    </span>
                                  );
                                })()}
                          </>
                        )}
                    </li>
                  </ul>
                </div>
              </div>

              {/* Column 3: Assignees + Details stacked (two Trello cards) */}
              <div className="flex flex-col gap-6">
                {/* Assignees */}
                <div className="rounded-lg bg-white shadow-sm ring-1 ring-black/5">
                  <div className="flex items-center gap-2 border-b border-neutral-200 px-4 py-3">
                    <span className="h-3 w-3 rounded-[3px] bg-cyan-500" />
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-700">
                      Assignees
                    </h3>
                  </div>
                  <div className="p-4">
                    {Array.isArray(selectedTask?.assignees) &&
                    selectedTask.assignees.length > 0 ? (
                      <Dropdown
                        trigger={["click"]}
                        dropdownRender={() => (
                          <div className="max-h-72 w-72 overflow-y-auto rounded-lg bg-white p-2 shadow-xl ring-1 ring-neutral-200">
                            {developerOptions.map((dev: any) => {
                              const isSelected = selectedTask.assignees.some(
                                (a: any) => a._id === dev._id,
                              );
                              return (
                                <div
                                  key={dev._id}
                                  onClick={() => {
                                    const newSelected = isSelected
                                      ? selectedTask.assignees.filter(
                                          (a: any) => a._id !== dev._id,
                                        )
                                      : [...selectedTask.assignees, dev];
                                    handleChange("assignees", newSelected);
                                  }}
                                  className={[
                                    "flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm transition",
                                    isSelected
                                      ? "bg-neutral-50"
                                      : "hover:bg-neutral-50",
                                  ].join(" ")}
                                >
                                  {dev.photo ? (
                                    <img
                                      src={`${IMAGEURL}/${dev.photo}`}
                                      alt={dev.username}
                                      className="h-8 w-8 rounded-full object-cover ring-1 ring-neutral-200"
                                    />
                                  ) : (
                                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-neutral-400 text-sm font-semibold text-white">
                                      {dev.username
                                        ?.trim()
                                        .charAt(0)
                                        .toUpperCase()}
                                    </div>
                                  )}
                                  <span className="text-neutral-800">
                                    {dev.username}
                                  </span>
                                  {isSelected && (
                                    <span className="ml-auto rounded-[6px] bg-emerald-500 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                                      Added
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      >
                        <div className="flex flex-wrap gap-2">
                          {selectedTask.assignees.map((user: any) => (
                            <div
                              key={user._id}
                              className="group flex items-center gap-2 rounded-[8px] bg-neutral-100 px-2.5 py-1.5 text-sm text-neutral-800"
                            >
                              {user.photo ? (
                                <img
                                  src={`${IMAGEURL}/${user.photo}`}
                                  alt={user.username}
                                  className="h-6 w-6 rounded-full object-cover ring-1 ring-neutral-300"
                                />
                              ) : (
                                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-neutral-400 text-[11px] font-semibold text-white">
                                  {user.username
                                    ?.trim()
                                    .charAt(0)
                                    .toUpperCase()}
                                </div>
                              )}
                              <span>{user.username}</span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const newAssignees =
                                    selectedTask.assignees.filter(
                                      (a: any) => a._id !== user._id,
                                    );
                                  handleChange("assignees", newAssignees);
                                }}
                                className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded hover:bg-neutral-200"
                                aria-label="Remove assignee"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </Dropdown>
                    ) : (
                      <Dropdown
                        trigger={["click"]}
                        dropdownRender={() => (
                          <div className="max-h-72 w-72 overflow-y-auto rounded-lg bg-white p-2 shadow-xl ring-1 ring-neutral-200">
                            {developerOptions.map((dev: any) => (
                              <div
                                key={dev._id}
                                onClick={() => handleChange("assignees", [dev])}
                                className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm text-neutral-800 transition hover:bg-neutral-50"
                              >
                                {dev.photo ? (
                                  <img
                                    src={`${IMAGEURL}/${dev.photo}`}
                                    alt={dev.username}
                                    className="h-8 w-8 rounded-full object-cover ring-1 ring-neutral-200"
                                  />
                                ) : (
                                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-neutral-400 text-sm font-semibold text-white">
                                    {dev.username
                                      ?.trim()
                                      .charAt(0)
                                      .toUpperCase()}
                                  </div>
                                )}
                                <span>{dev.username}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      >
                        <p className="cursor-pointer rounded-md border border-dashed border-neutral-300 bg-neutral-50 px-3 py-2 text-sm text-neutral-600 hover:bg-neutral-100">
                          Click to assign someone
                        </p>
                      </Dropdown>
                    )}
                  </div>
                </div>

                {/* Details */}
                <div className="rounded-lg bg-white shadow-sm ring-1 ring-black/5">
                  <div className="flex items-center gap-2 border-b border-neutral-200 px-4 py-3">
                    <span className="h-3 w-3 rounded-[3px] bg-amber-500" />
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-700">
                      Details
                    </h3>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-3">
                      <span className="min-w-[70px] text-sm font-medium text-neutral-700">
                        Priority
                      </span>
                      <Select
                        value={selectedTask?.priority || "Low"}
                        onChange={(value) => handleChange("priority", value)}
                        className="w-full [&_.ant-select-selector]:!rounded-md [&_.ant-select-selector]:!border [&_.ant-select-selector]:!border-neutral-300 [&_.ant-select-selector]:!bg-white [&_.ant-select-selector]:!py-1.5 [&_.ant-select-selection-item]:!text-neutral-800"
                        dropdownStyle={{ minWidth: 180 }}
                        bordered
                        suffixIcon={null}
                        dropdownClassName="rounded-md shadow-xl"
                      >
                        <Select.Option value="Low">
                          <div className="flex items-center gap-2">
                            <span className="inline-block h-2.5 w-6 rounded-[6px] bg-emerald-500" />
                            <span className="text-sm font-medium text-neutral-800">
                              Low
                            </span>
                          </div>
                        </Select.Option>
                        <Select.Option value="Medium">
                          <div className="flex items-center gap-2">
                            <span className="inline-block h-2.5 w-6 rounded-[6px] bg-amber-500" />
                            <span className="text-sm font-medium text-neutral-800">
                              Medium
                            </span>
                          </div>
                        </Select.Option>
                        <Select.Option value="High">
                          <div className="flex items-center gap-2">
                            <span className="inline-block h-2.5 w-6 rounded-[6px] bg-rose-500" />
                            <span className="text-sm font-medium text-neutral-800">
                              High
                            </span>
                          </div>
                        </Select.Option>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Board footer divider like Trello */}
            <div className="h-px w-full bg-gradient-to-r from-transparent via-neutral-300 to-transparent" />
          </div>
        ) : (
          <div className="py-14 text-center text-base italic text-neutral-400">
            Loading task details...
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={!!deleteId} // <-- This makes the modal open when deleteId has a value
        onOk={handleDelete}
        onCancel={() => setDeleteId(null)}
        okText="Yes"
        cancelText="No"
        // footer={null}
        width={500}
        centered={false}
        destroyOnClose
        className="custom-modal"
        style={{
          bottom: 20,
          top: "10vw",
          maxHeight: "calc(100vh - 30px)",
          margin: "0 auto",
          position: "relative",
        }}
        title={
          <div
            style={{
              marginLeft: "11px",
              width: "100%",
              paddingRight: 20,
            }}
          >
            Confirmation
          </div>
        }
        okButtonProps={{
          style: {
            backgroundColor: "#ff4d4f",
            borderColor: "#ff4d4f",
            color: "white",
          },
        }}
        bodyStyle={{
          padding: "22px",
          overflowY: "auto",
          fontFamily: "inter, sans-serif",
        }}
        styles={{
          header: {
            borderBottom: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
          footer: {
            borderTop: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
        }}
      >
        <p>Are you sure you want to delete this Task?</p>
      </Modal>

      {/* Task Log Modal */}
      <Modal
        title={
          <div className="flex justify-between items-center w-full">
            <span className="text-xl font-bold">Task Log</span>
            <Input
              placeholder="🔍 Search logs..."
              allowClear
              value={logSearchText}
              onChange={(e) => setLogSearchText(e.target.value)}
              className="w-64 border border-gray-300 rounded px-2 py-1 text-sm mr-5"
            />
          </div>
        }
        open={isLogModalOpen}
        onCancel={() => setIsLogModalOpen(false)}
        footer={null}
        width="100%" // ✅ Makes modal responsive
        style={{ maxWidth: "800px", margin: "0 auto" }}
      >
        {selectedTaskLogs.length > 0 ? (
          <div className="overflow-auto max-h-96">
            <table className="w-full border-collapse border border-gray-400 bg-white shadow-md rounded-lg">
              <thead>
                <tr className="bg-gray-200">
                  <th className="border p-3 text-left w-1/3">
                    <ClockCircleOutlined className="mr-2" /> Updated At
                  </th>
                  <th className="border p-3 text-left">
                    <MessageOutlined className="mr-2" /> Message
                  </th>
                </tr>
              </thead>
              <tbody>
                {selectedTaskLogs
                  .filter((log) =>
                    log.message
                      .toLowerCase()
                      .includes(logSearchText.toLowerCase()),
                  )
                  .map((log, index) => {
                    const formattedTimestamp = dayjs(log.createdAt)
                      .utc()
                      .local()
                      .format("DD-MM-YYYY HH:mm");
                    return (
                      <tr key={index} className="hover:bg-gray-100 transition">
                        <td className="border p-3">{formattedTimestamp}</td>
                        <td className="border p-3">{log.message}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
            <Pagination
              current={page} // Current page
              pageSize={10} // Items per page
              showSizeChanger={false}
              total={filteredData.length} // Total items
              onChange={(pageNumber: any) => setPage(pageNumber)} // Handle page change
            />
          </div>
        ) : (
          <p className="text-center text-gray-500">
            No logs found for this task.
          </p>
        )}
      </Modal>
      {editModalVisible && (
        <EditTaskModal
          visible={editModalVisible}
          taskId={currentTaskId}
          onCancel={() => setEditModalVisible(false)}
          onSuccess={() => {
            setEditModalVisible(false);
            fetchTasks();
          }}
        />
      )}
    </div>
  );
};
